import * as React from "react";
import {TableCell, TableSortLabel} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useAuth} from "../context/AuthContext";
import {API_BASE_URL} from "../config/apiConfig";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Branch {
    id: string;
    code: string;
    name: string;
    city: string;
    region: string;
    isActive: boolean;
    status: string;
    updatedAt: string;
    attributes?: Record<string, string[]>;
}

// Pick the first value from attributes map, fallback to direct field, then "-"
export function attr(branch: Branch, key: string, fallback?: string): string {
    const list = branch.attributes?.[key];
    if (list && list.length > 0) return list[0];
    return fallback ?? "-";
}

// Format an ISO-8601 UTC instant (e.g. 2026-08-07T16:29:50.475151100Z) as "2026-08-07 16:29 UTC".
// Parsed by pattern (not Date) so the 9-digit nanosecond fraction and timezone never shift the value.
export function formatUtcDateTime(value?: string | null): string {
    if (!value) return "-";
    const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(value);
    return m ? `${m[1]} ${m[2]}` : value;
}

// ─── Sorting helpers ──────────────────────────────────────────────────────────

export type Order = "asc" | "desc";
export type SortableKey = "code" | "name" | "city" | "region" | "status" | "updatedAt";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
    const aVal = a[orderBy] ?? "";
    const bVal = b[orderBy] ?? "";
    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
}

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// ─── SortCell helper ──────────────────────────────────────────────────────────

export function SortCell({label, field, orderBy, order, onSort}: Readonly<{
    label: string;
    field: SortableKey;
    orderBy: SortableKey;
    order: Order;
    onSort: (f: SortableKey) => void;
}>) {
    return React.createElement(
        TableCell,
        {sortDirection: orderBy === field ? order : false},
        React.createElement(
            TableSortLabel,
            {
                active: orderBy === field,
                direction: orderBy === field ? order : "asc",
                onClick: () => onSort(field),
            },
            label,
        ),
    );
}

// ─── Status chip color helper ─────────────────────────────────────────────────

export function getStatusColor(status: string): "success" | "error" | "default" {
    if (status?.toUpperCase() === "APPROVED") return "success";
    if (status?.toUpperCase() === "REJECTED") return "error";
    return "default";
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function useBranches() {
    const {token} = useAuth();
    const {t} = useTranslation();

    const [branches, setBranches] = React.useState<Branch[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);

    // Search
    const [searchInput, setSearchInput] = React.useState<string>("");
    const [searchQuery, setSearchQuery] = React.useState<string>("");

    // Selection
    const [selected, setSelected] = React.useState<string[]>([]);

    // Sorting
    const [order, setOrder] = React.useState<Order>("asc");
    const [orderBy, setOrderBy] = React.useState<SortableKey>("name");

    // Pagination
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const itemsPerPage = 10;

    // Action menu
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [menuRowId, setMenuRowId] = React.useState<string | null>(null);
    const menuOpen = Boolean(anchorEl);

    // Add Branch dialog
    const [addDialogOpen, setAddDialogOpen] = React.useState<boolean>(false);
    const [newCode, setNewCode] = React.useState<string>("");
    const [newName, setNewName] = React.useState<string>("");
    const [newCity, setNewCity] = React.useState<string>("");
    const [newRegion, setNewRegion] = React.useState<string>("");
    const [newIsActive, setNewIsActive] = React.useState<boolean>(true);

    // Form validation errors
    const [formErrors, setFormErrors] = React.useState<{
        code?: string;
        name?: string;
        city?: string;
        region?: string
    }>({});

    // Edit Branch dialog
    const [editDialogOpen, setEditDialogOpen] = React.useState<boolean>(false);
    const [editId, setEditId] = React.useState<string>("");
    const [editCode, setEditCode] = React.useState<string>("");
    const [editName, setEditName] = React.useState<string>("");
    const [editCity, setEditCity] = React.useState<string>("");
    const [editRegion, setEditRegion] = React.useState<string>("");
    const [editStatus, setEditStatus] = React.useState<string>("");
    const [editIsActive, setEditIsActive] = React.useState<boolean>(true);
    const [editFormErrors, setEditFormErrors] = React.useState<{
        code?: string;
        name?: string;
        city?: string;
        region?: string
    }>({});

    // Success message
    const [showSuccessMessage, setShowSuccessMessage] = React.useState<boolean>(false);
    const [successMessageText, setSuccessMessageText] = React.useState<string>("");

    // ─── Data fetch ─────────────────────────────────────────────────────────────

    const fetchBranches = React.useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/branches/get`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!response.ok) throw new Error(`Failed to fetch branches: ${response.statusText}`);
            const data = await response.json();
            setBranches(data.tenants ?? data.branches ?? data ?? []);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.branches.error.fetchFailed'));
            setBranches([]);
        } finally {
            setLoading(false);
        }
    }, [token, t]);

    React.useEffect(() => {
        fetchBranches();
    }, [fetchBranches]);

    // ─── Filtering, Sorting & Pagination ─────────────────────────────────────────

    const handleRequestSort = (field: SortableKey) => {
        const isAsc = orderBy === field && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(field);
    };

    const filteredBranches = React.useMemo(() => {
        if (!searchQuery.trim()) return branches;
        const q = searchQuery.toLowerCase();
        return branches.filter(
            (b) =>
                b.name?.toLowerCase().includes(q) ||
                b.code?.toLowerCase().includes(q) ||
                b.city?.toLowerCase().includes(q) ||
                b.region?.toLowerCase().includes(q),
        );
    }, [branches, searchQuery]);

    const sortedBranches = React.useMemo(
        () => [...filteredBranches].sort(getComparator(order, orderBy)),
        [filteredBranches, order, orderBy],
    );

    const paginatedBranches = React.useMemo(
        () => sortedBranches.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
        [sortedBranches, currentPage],
    );

    // ─── Selection ───────────────────────────────────────────────────────────────

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelected(filteredBranches.map((b) => b.id));
        } else {
            setSelected([]);
        }
    };

    const handleRowClick = (id: string) => {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
        );
    };

    const isSelected = (id: string) => selected.includes(id);

    // ─── Action menu ─────────────────────────────────────────────────────────────

    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, id: string) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
        setMenuRowId(id);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuRowId(null);
    };

    const handleMenuAction = (action: () => void) => {
        action();
        handleMenuClose();
    };

    // ─── Status update (Approve / Reject) ───────────────────────────────────────

    const handleStatusUpdate = async (ids: string[], status: string) => {
        if (!token || ids.length === 0) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/branches/status`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    tenantStatuses: ids.map((branchId) => ({branchId, status})),
                }),
            });
            if (!response.ok) throw new Error(`Failed to update status: ${response.statusText}`);
            setSelected([]);
            const statusKey = status === "APPROVED" ? "hr.branches.success.approved" : "hr.branches.success.rejected";
            setSuccessMessageText(t(statusKey, {count: ids.length}));
            setShowSuccessMessage(true);
            await fetchBranches();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.branches.error.statusUpdateFailed'));
        }
    };

    // ─── Edit dialog helpers ─────────────────────────────────────────────────────

    const handleEditOpen = (row: Branch) => {
        setEditId(row.id);
        setEditCode(attr(row, "code", row.code));
        setEditName(row.name ?? "");
        setEditCity(attr(row, "city", row.city));
        setEditRegion(attr(row, "region", row.region));
        setEditStatus(attr(row, "status", row.status));
        setEditIsActive(attr(row, "is_active", String(row.isActive)).toUpperCase() === "TRUE");
        setEditFormErrors({});
        setEditDialogOpen(true);
    };

    const handleEditClose = () => {
        setEditDialogOpen(false);
        setEditId("");
        setEditCode("");
        setEditName("");
        setEditCity("");
        setEditRegion("");
        setEditStatus("");
        setEditIsActive(true);
        setEditFormErrors({});
    };

    const validateEditForm = (): boolean => {
        const errors: { code?: string; name?: string; city?: string; region?: string } = {};
        if (!editCode.trim()) errors.code = t('hr.branches.validation.codeRequired');
        if (!editName.trim()) errors.name = t('hr.branches.validation.nameRequired');
        if (!editCity.trim()) errors.city = t('hr.branches.validation.cityRequired');
        if (!editRegion.trim()) errors.region = t('hr.branches.validation.regionRequired');
        setEditFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleEditSave = async () => {
        if (!validateEditForm() || !token) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/branches/update`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    id: editId,
                    name: editName,
                    city: editCity,
                    code: editCode,
                    region: editRegion,
                    status: editStatus,
                    active: editIsActive,
                }),
            });
            if (!response.ok) throw new Error(`Failed to update branch: ${response.statusText}`);
            handleEditClose();
            setSuccessMessageText(t('hr.branches.success.updated'));
            setShowSuccessMessage(true);
            await fetchBranches();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.branches.error.updateFailed'));
        }
    };

    // ─── Dialog helpers ──────────────────────────────────────────────────────────

    const handleCloseDialog = () => {
        setAddDialogOpen(false);
        setNewCode("");
        setNewName("");
        setNewCity("");
        setNewRegion("");
        setNewIsActive(true);
        setFormErrors({});
    };

    const validateForm = (): boolean => {
        const errors: { code?: string; name?: string; city?: string; region?: string } = {};
        if (!newCode.trim()) errors.code = t('hr.branches.validation.codeRequired');
        if (!newName.trim()) errors.name = t('hr.branches.validation.nameRequired');
        if (!newCity.trim()) errors.city = t('hr.branches.validation.cityRequired');
        if (!newRegion.trim()) errors.region = t('hr.branches.validation.regionRequired');
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    return {
        token,
        branches,
        loading,
        error,
        setError,
        searchInput,
        setSearchInput,
        searchQuery,
        setSearchQuery,
        selected,
        order,
        orderBy,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        anchorEl,
        menuRowId,
        menuOpen,
        addDialogOpen,
        setAddDialogOpen,
        newCode,
        setNewCode,
        newName,
        setNewName,
        newCity,
        setNewCity,
        newRegion,
        setNewRegion,
        newIsActive,
        setNewIsActive,
        formErrors,
        setFormErrors,
        editDialogOpen,
        editId,
        editCode,
        setEditCode,
        editName,
        setEditName,
        editCity,
        setEditCity,
        editRegion,
        setEditRegion,
        editStatus,
        editIsActive,
        setEditIsActive,
        editFormErrors,
        setEditFormErrors,
        showSuccessMessage,
        setShowSuccessMessage,
        successMessageText,
        setSuccessMessageText,
        fetchBranches,
        handleRequestSort,
        filteredBranches,
        sortedBranches,
        paginatedBranches,
        handleSelectAll,
        handleRowClick,
        isSelected,
        handleMenuOpen,
        handleMenuClose,
        handleMenuAction,
        handleStatusUpdate,
        handleEditOpen,
        handleEditClose,
        validateEditForm,
        handleEditSave,
        handleCloseDialog,
        validateForm,
    };
}
